import { useEffect } from 'react';
import { useUIStore } from '@/stores/uiStore';

/**
 * Applies the persisted `uiStore.theme` to the document by setting the
 * `data-theme` attribute on `<html>` (matching the `[data-theme='dark']`
 * selector wired up in `globals.css`). For `theme === 'system'`, watches
 * `prefers-color-scheme` and updates accordingly. Mount once at the app root.
 */
export function useThemeEffect(): void {
  const theme = useUIStore((s) => s.theme);

  useEffect(() => {
    const root = document.documentElement;

    const apply = (mode: 'light' | 'dark') => {
      root.setAttribute('data-theme', mode);
      // Keep `.dark` class in sync as well, so any Tailwind `dark:` utilities
      // that happen to ship in the design system also work without surprise.
      if (mode === 'dark') root.classList.add('dark');
      else root.classList.remove('dark');
      // Browser/PWA chrome follows the app surface — read --bg AFTER the
      // data-theme flip so the resolved value is the active theme's.
      const bg = getComputedStyle(root).getPropertyValue('--bg').trim();
      if (bg) {
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg);
      }
    };

    if (theme === 'light' || theme === 'dark') {
      apply(theme);
      return;
    }

    // theme === 'system' — track the media query
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      apply('light');
      return;
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => apply(mql.matches ? 'dark' : 'light');
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, [theme]);
}
